/**
 * shareUrl — build client-facing URLs that prefer the studio's
 * live custom domain over `window.location.origin`.
 *
 * Every link that leaves the studio and lands in front of a client —
 * a gallery, a delivery, a contract to sign, a document, a video
 * proof, a booking page, an embedded intake form — is built from
 * `resolveShareOrigin` so the URL wears the studio's brand.
 *
 * Two shapes, same lookup and same cache:
 *
 *   • `resolveShareOrigin(orgId)` — the origin on its own. Used by
 *     the `useShareOrigin` hook, which is what render-time and
 *     clipboard call sites want (see that file for why).
 *   • `buildShareUrl(path, orgId)` — origin + path, for call sites
 *     already sitting in an async function.
 *
 * Design notes:
 *
 *   • Fetches on demand — the custom hostname lookup is a single
 *     row read against `custom_domains_public` filtered on the
 *     studio's org. `useOrgId`'s cache keeps this cheap in
 *     practice, and the lookup only fires when the user actually
 *     copies a link.
 *
 *   • Falls back to `window.location.origin` on any error, on no
 *     verified domain, or when `orgId` is missing. Studios without
 *     a custom domain keep the current behavior.
 *
 *   • Never returns an Orbit-owned host prefixed with the studio's
 *     branding — a bug earlier in the flow would fall through to
 *     the origin, not concoct a URL.
 */

import { supabase } from '@/integrations/supabase/client';

const cache = new Map<string, string | null>();

/**
 * Async: the origin client links should be built on — the studio's
 * live custom domain when `orgId` is given and they have one, else
 * `window.location.origin`. Never a trailing slash.
 *
 * `custom_domains_public` publishes only rows at status 'live'. A
 * domain whose DNS resolves but that Orbit hasn't provisioned stays
 * out of that view deliberately (P11, 20260812120000) — it would
 * hand out a hostname with no certificate — so this falls back to
 * the app's own origin rather than producing a link that breaks.
 */
export async function resolveShareOrigin(
  orgId: string | null | undefined,
): Promise<string> {
  const fallback = window.location.origin;
  if (!orgId) return fallback;

  // Per-org hostname cache — the domain rarely changes and this
  // spares us a round-trip on every Copy click in a session.
  let hostname: string | null;
  if (cache.has(orgId)) {
    hostname = cache.get(orgId) ?? null;
  } else {
    try {
      const { data, error } = await (supabase as any)
        .from('custom_domains_public')
        .select('hostname')
        .eq('org_id', orgId)
        .maybeSingle();
      if (error) throw error;
      hostname = (data?.hostname as string | undefined) ?? null;
      cache.set(orgId, hostname);
    } catch (err) {
      console.error('[shareUrl] custom-domain lookup failed:', err);
      hostname = null;
      cache.set(orgId, null);
    }
  }
  return hostname ? `https://${hostname}` : fallback;
}

/**
 * Async: the best available share URL for a client-facing path
 * (`/gallery/abc`, `/delivery/xyz`, `/sign/…`, `/d/…`, `/book/…`).
 * `path` must start with a slash.
 */
export async function buildShareUrl(
  path: string,
  orgId: string | null | undefined,
): Promise<string> {
  return `${await resolveShareOrigin(orgId)}${path}`;
}

/** Invalidate the cached lookup — call after a studio deletes /
 *  changes their custom domain so subsequent copies use the new
 *  URL without a page reload. */
export function clearShareUrlCache(orgId?: string): void {
  if (orgId) {
    cache.delete(orgId);
  } else {
    cache.clear();
  }
}
